import { OwcChip, OwcGrid, OwcTooltip, OwcTypography } from "@one/react";
import CustomChip from "../CustomChip";
import { EQUIPMENT_DETAILS_HEADING } from "../../../constants";
import { valueOrEmpty } from "../../../utils/helpers/text";
import { useEffect, useState } from "react";
import { fetchDocumentationStatus } from "../../../features/equipment-list/DocumentationRender";
import DocumentationModal from "../../../features/equipment-list/DocumentationModal";
import ChecklistFolder from "../../../icons/ChecklistFolder";
import BookingAccessControlRender from "../../../features/equipment-list/BookingAccessControlRender";
import { visibleAttributeKeys } from "../../../utils/helpers/vnvEquipment";

const PopoverAttributesDetails = ({
  data,
  title,
  metaData,
}) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [documents, setDocuments] = useState([]);

  useEffect(() => {
    if (!data?.siteName || !data?.belongingToGroup || !data?.equipmentModel) {
      return;
    }

    setLoading(true);
    fetchDocumentationStatus({
      model: data.equipmentModel,
      site: data.siteName,
      group: data.belongingToGroup,
    })
      .then((items) => setDocuments(items))
      .catch((err) => console.error("Documentation fetch error:", err))
      .finally(() => setLoading(false));
  }, [data?.siteName, data?.belongingToGroup, data?.equipmentModel]);

  const handleOpenModal = () => {
    setModalOpen(true);
  };

  const displayDataInChipsOrText = (key, equipmentData) => {
    if (key === "checklists") {
      if (loading || !documents?.length) {
        return "-";
      }
      return (
        <button
          onClick={handleOpenModal}
          style={{
            background: "none",
            border: "none",
            cursor: "pointer",
            padding: 0,
            display: "inline-flex",
            alignItems: "center",
            marginTop: "4px",
          }}
          title="View documentation"
        >
          <ChecklistFolder size={20} />
        </button>
      );
    }

    if (key === "qualificationStatus" || key === "systemStatus") {
      let value = valueOrEmpty(equipmentData[key], false, "-");
      return value !== "-" ? (
        <div>
          <OwcChip outlined id={`attr_${value}`}>
            {value}{" "}
          </OwcChip>
          <OwcTooltip anchor={`attr_${value}`}>{value}</OwcTooltip>
        </div>
      ) : (
        value
      );
    } else {
      return (
        <div>
          <OwcTypography variant="button" id={`attr_${key}`}>
            {valueOrEmpty(equipmentData[key], false, "-")}
          </OwcTypography>
          <OwcTooltip anchor={`attr_${key}`}>{valueOrEmpty(equipmentData[key], false, "-")}</OwcTooltip>
        </div>
      );
    }
  };

  const renderAccessControlValue = () => {
    if (!data) {
      return "-";
    }
    return <BookingAccessControlRender data={data} />;
  };

  // V&V equipment swaps Nickname and Manufacturer, which P1S V&V records never carry, for V&V
  // Status. Filtered before the access control splice so the checklist index stays correct.
  const rawMetaKeys = visibleAttributeKeys(Object.keys(metaData || {}), data);
  const documentationIndex = rawMetaKeys.indexOf("checklists");
  const shouldShowAccessControl = Boolean(data);
  const orderedMetaKeys = [...rawMetaKeys];

  if (shouldShowAccessControl) {
    if (documentationIndex >= 0) {
      orderedMetaKeys.splice(documentationIndex + 1, 0, "__accessControl__");
    } else {
      orderedMetaKeys.push("__accessControl__");
    }
  }

  return (<>
    <div style={{ padding: "0 1rem" }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', margin: '1.125rem 0' }}>
        <OwcTypography variant="button">
          {title}
        </OwcTypography>
        {(() => {
          if (title !== EQUIPMENT_DETAILS_HEADING) {
            return null;
          }
          let booked = Array.isArray(data?.bookedEquipments) ? data.bookedEquipments : null;
          if (!booked || booked.length === 0) {
            if (Array.isArray(data?.bookingEntries)) {
              booked = Array.from(new Set(
                data.bookingEntries.map((entry) => (entry?.equipment?.shortIdentifier || entry?.resource)).filter(Boolean)
              ));
            }
          }
          if (booked && booked.length >= 1) {
            return (
              <CustomChip
                text={`${booked.length}`}
                color="#fff"
                bgColor="rgba(11, 65, 205, 1)"
                compact
                info="Total items in this booking"
              />
            );
          }
          return null;
        })()}
      </div>

      <OwcGrid container columns="4" vGutter={{ m: 10, l: 15, xl: 20 }}>
        {orderedMetaKeys.map((key) => {
          if (key === "__accessControl__") {
            return (
              <OwcGrid item l={1} m={1} xs={1} s={1} key="popover_access_control" style={{ marginBottom: 16 }}>
                <OwcTypography variant="caption">Access control</OwcTypography>
                <span>{renderAccessControlValue()}</span>
              </OwcGrid>
            );
          }

          return (
            <OwcGrid item l={1} m={1} xs={1} s={1} key={metaData[key].key} style={{ marginBottom: 16 }}>
              <OwcTypography variant="caption">{metaData[key].value}</OwcTypography>
              <span
                style={{
                  display: "-webkit-box",
                  WebkitLineClamp: 1,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {displayDataInChipsOrText(key, data)}
              </span>
            </OwcGrid>
          );
        })}
      </OwcGrid>
    </div>

    <DocumentationModal
      visible={modalOpen}
      onClose={() => setModalOpen(false)}
      equipmentDetails={data}
      documents={documents}
      loading={loading}
    /></>
  );
};

export default PopoverAttributesDetails;
